/* eslint-disable @typescript-eslint/no-explicit-any */
import * as History from 'history';
import { ACTION_LOGIN, ACTION_SIGNUP, UserProperty } from 'utils/analytics/eventTypesConstants';
import { EventCustomAttributes } from 'utils/analytics/eventTypeConstantsAmplitude';
import { Network } from 'components/OAuth';
import { PURCHASE } from './analytics/eventTypeConstantsAmplitude';
import { PaymentMethod } from 'forms/payment-method/PaymentMethodForm';
import {
  amplitudeIncrementUserProperty,
  amplitudeSendEvent,
  amplitudeSetUserProperty,
  businessToData,
  experienceToData,
  paymentMethodToData,
  purchaseOrderToData,
} from 'utils/analytics/amplitude';
import { assertNever } from '@tock/shared/utils/typeUtils';
import { consumer, core, safeToNumber } from '@tock/coretypes';
import { fbPurchase } from 'utils/analytics/fbq';
import { ga4SendConversionEvent, ga4SendPurchaseEvent } from 'utils/analytics/ga4';
import {
  ga4SendEvent,
  ga4SetUserProperty,
  getGaWrapper,
  getGtagWrapper,
} from 'components/consent-restricted-scripts/GoogleAnalytics';
import { isEmpty, pick } from 'es-toolkit/compat';
import { logger } from '@tock/logging';
import { uaSendConversionEvent, uaSendPurchaseEvent, uaTransitionToPage } from 'utils/analytics/ua';
import QueryString from '@tock/query-string';

export interface PaymentMethodDetails {
  paymentMethod?: PaymentMethod;
  availablePaymentMethods?: PaymentMethod[];
  isSavedCreditCard?: boolean;
  paymentProcessorType?: core.PaymentProcessorType;
  isSupportedApplePayEnvironment?: boolean;
}

//TODO: Add gatrack and fbqtrack exports
export interface Analytics {
  pushGoogleOptimizeEvent: (eventName: string) => void;
  sendEvent: (
    eventCategory: any,
    eventAction: any,
    eventLabel: any,
    eventValue: any,
    fieldsObject: any
  ) => void;
  sendAuthEvent: (
    authEventType: typeof ACTION_LOGIN | typeof ACTION_SIGNUP,
    patron: core.IPatron | undefined,
    options?: { network?: Network; checkoutProperties?: EventCustomAttributes }
  ) => void;
  sendPurchaseEvent: (
    business: consumer.BusinessConfig,
    purchaseOrder: core.PurchaseOrder,
    ticketType?: core.TicketType,
    paymentMethodDetails?: PaymentMethodDetails
  ) => void;
  setDimensionInTockOnly: (dimensionName: any, dimensionValue: any) => void;
  setUserProperty: (userProperty: UserProperty) => void;
  setExperiment: (experimentName: string) => void;
  transitionToPage: (location: Location) => void;
  fbqTrack: (...args: any[]) => any;
  gaTrack: (...args: any[]) => any;
}
const allowedParams = [
  'experience',
  'experienceId',
  'gclid',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'size',
  'date',
  'time',
  'q',
  'award',
  'city',
  'country',
  'cuisine',
  'cuisines',
  'priceRange',
  'syndicate',
  'tab',
];

export const customDimensions = {
  // "Booked Percent" in the Tock consumer GA account
  BOOKED_PERCENT: 'dimension2',
  // "Number of Experiences" in the Tock consumer GA account
  NUM_EXPERIENCES: 'dimension3',
  // "Has External Link" in the Tock consumer GA account
  HAS_EXTERNAL_LINK: 'dimension4',
  // "Tock App" in the Tock consumer GA account
  TOCK_APP: 'dimension5',
  // "Selected Metro Area" in the Tock consumer GA account
  SELECTED_METRO_AREA: 'dimension6',

  TOCK_SOURCE: 'dimension7',

  TOCK_MEDIUM: 'dimension8',
};

export const pathSanitizer = (location: History.Location | Location) => {
  const pathname = location.pathname;
  const queryString = location.search;
  const query = QueryString.parse(queryString);
  const sanitizedQuery = pick(query, allowedParams);

  return `${pathname}${isEmpty(sanitizedQuery) ? '' : '?' + QueryString.stringify(sanitizedQuery)}`;
};

const getSubcategory = (
  purchaseOrder: core.PurchaseOrder,
  ticketType?: core.TicketType
): string | undefined => {
  if (purchaseOrder.visitEditToken) {
    return 'Tableside';
  }
  if (!ticketType) {
    return undefined;
  }
  switch (ticketType.variety) {
    case 'DEPOSIT':
      return 'Deposit';
    case 'FULL':
      return 'Prix Fixe';
    case 'RESERVATION':
      return 'Reservation';
    case 'DEPRECATED_EVENT':
    case 'SEATED_EVENT':
    case 'GA_EVENT':
      return 'Event';
    case 'DEPRECATED_SPECIAL_TABLE':
      return 'Special tables';
    case 'ORDER_DELIVERY':
      switch (ticketType.deliveryServiceProvider) {
        case 'INTERNAL':
          return 'Delivery/Internal';
        case 'DOORDASH':
          return 'Delivery/DoorDash';
        case 'POSTMATES':
          return 'Delivery/Postmates';
        default:
          return assertNever(ticketType.deliveryServiceProvider);
      }
    case 'ORDER_PICKUP':
      return 'Pickup';
    case 'TABLESIDE':
      return 'Tableside';
    default:
      return assertNever(ticketType.variety);
  }
};

export enum AnalyticsEventQueue {
  GA = 'tockGaQueue',
  FB = 'tockFbQueue',
  GTAG = 'tockGtagQueue',
}

const defaults = {
  pushGoogleOptimizeEvent: (eventName: string) => {
    logger.debug('Analytics.pushGoogleOptimizeEvent', eventName);
  },
  setUserProperty: (...args: any[]) => {
    logger.debug('Analytics.setUserProperty', ...args);
  },
  sendAuthEvent: (...args: any[]) => {
    logger.debug('Analytics.sendAuthEvent', ...args);
  },
  sendEvent: (...args: any[]) => {
    logger.debug('Analytics.sendEvent', ...args);
  },
  sendPurchaseEvent: () => {
    logger.debug('Analytics.sendPurchaseEvent');
  },
  transitionToPage: (location: Location) => {
    logger.debug('Analytics.transitionToPage', pathSanitizer(location));
  },
  setDimensionInTockOnly: (...args: any[]) => {
    logger.debug('Analytics.setDimensionInTockOnly', ...args);
  },
  setExperiment: (...args: any[]) => {
    logger.debug('Analytics.setExperiment', ...args);
  },
  fbqTrack: (...args: any[]) => {
    logger.debug('Analytics.fbqTrack', ...args);
  },
  gaTrack: (...args: any[]) => {
    logger.debug('Analytics.gaTrack', ...args);
  },
};

// Managed by GoogleAnalytics component in component/consent-restricted-scripts
const getGaTrack = () => {
  if (window.analytics?.gaTrack) {
    return window.analytics.gaTrack;
  } else {
    return defaults.gaTrack;
  }
};

// Managed by FacebookPixel component in component/consent-restricted-scripts
const getFbqTrack = () => {
  if (window.analytics?.fbqTrack) {
    return window.analytics.fbqTrack;
  } else {
    return defaults.fbqTrack;
  }
};

const initializeDefault = () => defaults;

// Returns an object that contains useful tracking methods to be accessed in the tock domain
export function initialize(
  tockUaProperty: string | null,
  tockGa4StreamId: string | null,
  {
    businessUaProperty,
    businessGa4StreamId,
  }: {
    businessUaProperty?: string;
    businessGa4StreamId?: string;
  }
): Analytics {
  if (location.hostname === 'localhost' || tockUaProperty === null) {
    if (location.hostname !== 'localhost') {
      logger.warn('No web property was configured. Disabling Google Analytics.');
    } else {
      logger.debug('Google Analytics disabled because we are on localhost.');
    }
    return initializeDefault();
  } else {
    const restaurantUsingLatestGA = !!businessGa4StreamId;
    /* Wrapper functions to queue analytics when cookies not accepted yet */
    const shouldSendRestaurantPixel = location.hostname.indexOf('staging.exploretock.com') < 0;

    /**
     * Function to call upon routing to a new page.
     *
     * @param {object} location The location that is being transitioned to
     */
    const transitionToPage = (location: Location) => {
      uaTransitionToPage(getGaTrack(), pathSanitizer(location), location.origin);
      // This might not be necessary for GA4
      // const sendRestaurantData: boolean = !fastRoutingBetweenContext && shouldSendRestaurantPixel;
      // ga4TransitionToPage(
      //   gtagWrapper,
      //   tockWebProperty,
      //   restaurantWebProperty,
      //   sendRestaurantData,
      //   pathSanitizer(location),
      //   location.origin
      // );
      getFbqTrack()('trackSingle', 'PageView');
    };

    // ensures we only run the setExperiments code once per experiment
    const addedExperiments = new Set();

    /**
     * Function to call when setting an experiment.
     */
    const setExperiment = (experimentString: string) => {
      if (addedExperiments.has(experimentString)) {
        return;
      }
      getGaTrack()('set', 'exp', experimentString);
      getGaTrack()('send', 'event', 'experiment_impression', experimentString);

      getGtagWrapper()('set', { exp: experimentString });
      addedExperiments.add(experimentString);
    };

    /**
     * Send enchance ecommerce conversion tracking data to Google.
     */
    const sendPurchaseEvent = (
      business: consumer.BusinessConfig,
      purchaseOrder: core.PurchaseOrder,
      ticketType?: core.TicketType,
      paymentMethodDetails?: PaymentMethodDetails
    ) => {
      const purchaseCategory = getSubcategory(purchaseOrder, ticketType) ?? 'Unknown';
      uaSendPurchaseEvent(getGaWrapper(), purchaseOrder, purchaseCategory, business);
      uaSendConversionEvent(getGaWrapper(), purchaseOrder);
      ga4SendPurchaseEvent(
        getGtagWrapper(),
        purchaseOrder,
        purchaseCategory,
        business,
        ticketType,
        restaurantUsingLatestGA && shouldSendRestaurantPixel
      );
      ga4SendConversionEvent(
        getGtagWrapper(),
        purchaseOrder,
        restaurantUsingLatestGA && shouldSendRestaurantPixel
      );
      if (businessUaProperty && shouldSendRestaurantPixel) {
        uaSendPurchaseEvent(
          getGaWrapper(),
          purchaseOrder,
          purchaseCategory,
          business,
          'restaurant'
        );
        uaSendConversionEvent(getGaWrapper(), purchaseOrder, 'restaurant');
      }
      fbPurchase(getFbqTrack(), purchaseOrder, business);

      amplitudeSendEvent(PURCHASE, {
        ...businessToData(business),
        ...experienceToData(ticketType),
        ...purchaseOrderToData(purchaseOrder),
        ...paymentMethodToData(paymentMethodDetails),
        isOptedInBusinessMarketing: !!purchaseOrder.emailOptIn,
      });
      amplitudeIncrementUserProperty('Total reservations', 1);
      amplitudeIncrementUserProperty('Total reservation cents spent', purchaseOrder.totalPrice);
    };

    /*
     * Reference: https://developers.google.com/analytics/devguides/collection/analyticsjs/events
     */
    const sendEvent = (
      eventCategory: any,
      eventAction: any,
      eventLabel: any = null,
      eventValue: any = null,
      fieldsObject: any = null
    ) => {
      if (!eventCategory || !eventAction) {
        logger.error(
          'sendEvent requires an eventCategory and an eventAction (at least two parameters)'
        );
      }

      // We default to using transport: beacon, as browser support for the Beacon API has reached sufficient levels
      if (!fieldsObject) {
        fieldsObject = { transport: 'beacon' };
      } else if (!fieldsObject.transport || fieldsObject.transport !== 'beacon') {
        fieldsObject.transport = 'beacon';
      }

      getGaTrack()(
        'send',
        'event',
        eventCategory,
        eventAction,
        eventLabel,
        eventValue,
        fieldsObject
      );
      // Adding in a call for ga4 here will require more thought and understanding about the kinds of evens
      // that we're looking to track. Concepts of eventCategory, action, label, etc. don't apply there.
    };

    const setDimensionInTockOnly = (dimensionName: any, dimensionValue: any) => {
      getGaWrapper()('set', dimensionName, dimensionValue);
      getGtagWrapper()('set', { [dimensionName]: dimensionValue });
    };

    const setUserProperty = (userProperty: UserProperty) => {
      amplitudeSetUserProperty(userProperty);
      ga4SetUserProperty(userProperty);
    };

    const sendAuthEvent = (
      authEventType: typeof ACTION_LOGIN | typeof ACTION_SIGNUP,
      patron: core.IPatron | undefined,
      options?: { network?: Network; checkoutProperties?: EventCustomAttributes }
    ) => {
      const network = options?.network;
      // todo auth context (checkout, shop browsing, reservations browsing, modal vs page)
      const data = {
        auth: {
          network,
        },
        ...options?.checkoutProperties,
      };
      // Send event
      // TODO(jacob): Wrapper function that understands that some events will have different names
      //     across analytics systems
      amplitudeSendEvent(authEventType, data);
      ga4SendEvent(ga4StandardEventForAuthEvent(authEventType), data);
      if (network) {
        setUserProperty({ property: 'Auth network', value: network });
      }
      setUserProperty({
        property: 'Has giftcard balance',
        value: patron?.hasTockGiftCardBalance || false,
      });
      if (patron?.id) {
        // We don't forward Amplitude events to customers, so we don't need to restrict the
        // Amplitude side of things. But in GA4, we want to only set user IDs for our property, not
        // customers', so we can't use setUserProperty here and instead we need to call each tracker
        // manually
        amplitudeSetUserProperty({ property: 'PatronId', value: safeToNumber(patron.id) });
        getGtagWrapper()('config', tockGa4StreamId, {
          user_id: safeToNumber(patron.id),
        });
      }
    };

    // Object that allows us to bind analytics tracking to the router
    return {
      pushGoogleOptimizeEvent: (eventName: string) => {
        window.dataLayer.push({ event: eventName });
      },
      sendEvent,
      transitionToPage,
      setDimensionInTockOnly,
      setExperiment,
      setUserProperty,
      sendAuthEvent,
      sendPurchaseEvent,
      fbqTrack: getFbqTrack(),
      gaTrack: getGaTrack(),
    };
  }
}

/**
 * Converts our custom event names into GA4 standard event names for login and
 * signup events. (This helps us follow the GA4 recommendation that we send
 * standard events whenever possible to make our data more understandable and
 * to make the GA4 standard reports more useful.)
 */
function ga4StandardEventForAuthEvent(authEventType: typeof ACTION_LOGIN | typeof ACTION_SIGNUP) {
  switch (authEventType) {
    case 'Login':
      return 'login';
    case 'Signup':
      return 'sign_up';
    default:
      throw assertNever(authEventType);
  }
}
